import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Footer from '../landing-page/components/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { useAuth } from '../../contexts/AuthContext';

const LegalInformation = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('terms');

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Terms of Service', path: null }
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document?.getElementById(sectionId);
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {user ? (
        <>
          <Header />
          <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-24 max-w-7xl py-8 md:py-12 mt-16">
            <Breadcrumbs items={breadcrumbItems} />
          </div>
        </>
      ) : (
        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-24 max-w-7xl py-8 md:py-12">
          <button
            onClick={() => navigate('/landing-page')}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors mb-8 font-medium">
            <Icon name="ArrowLeft" size={20} />
            <span>Back to Home</span>
          </button>
        </div>
      )}

      <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-24 max-w-7xl py-8 md:py-12 mt-16">
        {/* Page Header */}
        <div className="mb-8 md:mb-12">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4">
            Legal Information
          </h1>
          <p className="text-lg text-muted-foreground">
            Last updated: January 17, 2026
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-3 mb-8 md:mb-12 border-b border-border pb-4">
          <button
            onClick={() => scrollToSection('terms')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeSection === 'terms' ?'bg-primary text-primary-foreground' :'bg-card text-card-foreground hover:bg-accent'
            }`}>
            Terms of Service
          </button>
          <button
            onClick={() => scrollToSection('privacy')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeSection === 'privacy' ?'bg-primary text-primary-foreground' :'bg-card text-card-foreground hover:bg-accent'
            }`}>
            Privacy Policy
          </button>
          <button
            onClick={() => scrollToSection('refund')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeSection === 'refund' ?'bg-primary text-primary-foreground' :'bg-card text-card-foreground hover:bg-accent'
            }`}>
            Refund Policy
          </button>
        </div>

        {/* Content Sections */}
        <div className="space-y-12 md:space-y-16">
          {/* Terms of Service */}
          <section id="terms" className="scroll-mt-24">
            <div className="bg-card rounded-xl p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl md:text-3xl font-heading font-bold mb-6 flex items-center gap-3">
                <Icon name="FileText" size={28} className="text-primary" />
                Terms of Service
              </h2>
              
              <div className="space-y-6 text-card-foreground">
                <div>
                  <h3 className="text-xl font-semibold mb-3">Welcome to Rovyn Budget</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    By using Rovyn Budget, you agree to these terms. We've written them in plain language to make sure you understand what you're agreeing to.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">Your Account</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    You're responsible for keeping your account secure and for all activity that happens under your account. Choose a strong password and don't share it with anyone.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    You must be at least 18 years old to use Rovyn Budget. By creating an account, you confirm that you meet this requirement.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">Subscription Terms</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    Rovyn Budget offers a <strong>Pro plan at $6 per month</strong>. This subscription gives you access to advanced features like smart alerts, payment calendar, deeper insights, and priority support.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    Your subscription automatically renews each month unless you cancel it. You can cancel anytime from your account settings, and you'll keep access until the end of your current billing period.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    We may adjust pricing with 30 days' notice. If you don't agree with the new price, you can cancel before it takes effect.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">What You Can and Can't Do</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    <strong>You can:</strong> Use Rovyn Budget to manage your personal finances, track expenses, set budgets, and gain insights into your spending.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    <strong>You can't:</strong> Share your account with others, use the service for illegal activities, attempt to hack or disrupt the platform, or scrape data from our system.
                  </p>
                </div>

                <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="AlertTriangle" size={24} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-amber-900 dark:text-amber-100 mb-2">Important Disclaimer</h4>
                      <p className="text-amber-800 dark:text-amber-200 leading-relaxed">
                        <strong>Rovyn Budget is not a financial advisor.</strong> We provide tools to help you track and manage your money, but we don't offer financial, investment, or legal advice. Always consult with a qualified professional for financial decisions.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">Educational Content Disclaimer</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    Rovyn Budget provides general financial education, budgeting tips, and informational content for educational purposes only. The information provided on this website, including blog posts, tools, and insights, does not constitute financial, investment, tax, or legal advice.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    Users should not rely on this information as a substitute for professional advice tailored to their personal financial situation. Rovyn Budget does not provide personalized financial recommendations or advisory services.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Always consider your personal circumstances and consult with a qualified financial professional before making any financial decisions.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">Termination</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    You can close your account anytime. We may suspend or terminate accounts that violate these terms or engage in harmful behavior. If we terminate your account, you'll receive a pro-rated refund for any unused subscription time.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">Changes to Terms</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    We may update these terms from time to time. We'll notify you of significant changes via email or through the app. Continuing to use Rovyn Budget after changes means you accept the new terms.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Privacy Policy */}
          <section id="privacy" className="scroll-mt-24">
            <div className="bg-card rounded-xl p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl md:text-3xl font-heading font-bold mb-6 flex items-center gap-3">
                <Icon name="Shield" size={28} className="text-primary" />
                Privacy Policy
              </h2>
              
              <div className="space-y-6 text-card-foreground">
                <div>
                  <h3 className="text-xl font-semibold mb-3">Your Privacy Matters</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    At Rovyn Budget, we take your privacy seriously. This policy explains what information we collect, how we use it, and how we protect it.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">Information We Collect</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    <strong>Account Information:</strong> When you sign up, we collect your name, email address, and password (which we encrypt).
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    <strong>Financial Data:</strong> You provide transaction details, account balances, budgets, and financial goals. This data stays in your account and is never shared without your permission.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    <strong>Usage Information:</strong> We collect data about how you use the app (like which features you use most) to improve the service.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">How We Use Your Information</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={20} className="text-primary flex-shrink-0 mt-1" />
                      <span>To provide and improve Rovyn Budget's features</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={20} className="text-primary flex-shrink-0 mt-1" />
                      <span>To send you important updates about your account or subscription</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={20} className="text-primary flex-shrink-0 mt-1" />
                      <span>To provide customer support when you need help</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={20} className="text-primary flex-shrink-0 mt-1" />
                      <span>To analyze usage patterns and make the app better</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">Data Protection & Security</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    We use <strong>banking-level encryption</strong> to protect your data. Your financial information is encrypted both when it's stored and when it's transmitted.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    We regularly update our security measures and monitor for suspicious activity. Our team follows strict security protocols to keep your data safe.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">Third-Party Services</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    We use trusted third-party services to help run Rovyn Budget (like payment processing and cloud hosting). These partners are carefully selected and required to protect your data.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    <strong>We never sell your personal information to anyone.</strong> Your financial data is yours, and we respect that.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">Your Rights</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    You have the right to:
                  </p>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={20} className="text-primary flex-shrink-0 mt-1" />
                      <span>Access all the data we have about you</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={20} className="text-primary flex-shrink-0 mt-1" />
                      <span>Request corrections to your data</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={20} className="text-primary flex-shrink-0 mt-1" />
                      <span>Delete your account and all associated data</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={20} className="text-primary flex-shrink-0 mt-1" />
                      <span>Export your data in a portable format</span>
                    </li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mt-3">
                    To exercise these rights, contact us at <a href="mailto:privacy@rovynbudget.com" className="text-primary hover:underline">privacy@rovynbudget.com</a>.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">Cookies & Tracking</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    We use cookies to keep you logged in and remember your preferences. You can control cookie settings in your browser, but some features may not work properly without them.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">Changes to Privacy Policy</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    If we make significant changes to this policy, we'll notify you via email and give you time to review the updates before they take effect.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">Contact Us</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    If you have questions about this Privacy Policy, please contact us at{' '}
                    <a href="mailto:privacy@rovynbudget.com" className="text-primary hover:underline">
                      privacy@rovynbudget.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Refund Policy */}
          <section id="refund" className="scroll-mt-24">
            <div className="bg-card rounded-xl p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl md:text-3xl font-heading font-bold mb-6 flex items-center gap-3">
                <Icon name="DollarSign" size={28} className="text-primary" />
                Refund Policy
              </h2>
              
              <div className="space-y-6 text-card-foreground">
                <div>
                  <h3 className="text-xl font-semibold mb-3">Our Commitment to Satisfaction</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    We want you to be happy with Rovyn Budget. If you're not satisfied with your Pro subscription, we offer a straightforward refund policy.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">30-Day Money-Back Guarantee</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    If you're not satisfied with your Pro subscription within the first 30 days, we'll give you a full refund — no questions asked.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Simply contact our support team at{' '}
                    <a href="mailto:support@rovynbudget.com" className="text-primary hover:underline">
                      support@rovynbudget.com
                    </a>
                    {' '}and we'll process your refund within 5-7 business days.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">After 30 Days</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    After the first 30 days, refunds are evaluated on a case-by-case basis. We understand that circumstances change, and we'll work with you to find a fair solution.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    You can cancel your subscription anytime, and you'll retain access until the end of your current billing period.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">How to Request a Refund</h3>
                  <ol className="space-y-3 text-muted-foreground list-decimal list-inside">
                    <li>Email us at support@rovynbudget.com with your account email</li>
                    <li>Include "Refund Request" in the subject line</li>
                    <li>Let us know why you're requesting a refund (optional but helpful)</li>
                    <li>We'll process your request within 1-2 business days</li>
                  </ol>
                </div>

                <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={24} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Important Note</h4>
                      <p className="text-blue-800 dark:text-blue-200 leading-relaxed">
                        Refunds are processed to the original payment method. Please allow 5-7 business days for the refund to appear in your account.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Contact Section */}
        <div className="mt-12 md:mt-16 bg-accent rounded-xl p-6 md:p-8 text-center">
          <h3 className="text-xl md:text-2xl font-heading font-bold mb-3">
            Questions About Our Policies?
          </h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            We're here to help. If you have any questions about these terms, privacy practices, or refund policies, please don't hesitate to reach out.
          </p>
          <a
            href="mailto:legal@rovynbudget.com"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors">
            <Icon name="Mail" size={20} />
            Contact Legal Team
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LegalInformation;